import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import Datagrid from "@ui/components/DataGrid";
import SearchBar from "@ui/components/SearchBar";
import { HEADINGS, searchMap } from "./constants";
import { removeTest, setTestsList, unArchiveTest } from "./_redux/actions";
import { renderTestsRow } from "./helpers";
import { ListButton } from "../../../../ui/components/ListButton";
import { ListHeader } from "../../../../ui/structures/ListHeader";
import { useFetch } from "../../../../hooks/fetch.hook";
import { deleteTest, getTestById, getTests, modifyTest } from "./_api";
import { ConfirmDelete } from "../../../../ui/components/ConfirmDelete";
import { Modal } from "../../../../ui/components/Modal";
import { ArchiveCheckbox } from "../../../../ui/components/ArchiveCheckbox";
import { getSelectedId, handleSearch } from "../../../../ui/helpers";
import { UnarchiveButton } from "../../../../ui/components/UnarchiveButton";
import { sortHeaderStyle } from "../../../constants";
import { TableSortLabel } from "@material-ui/core";

export const List = () => {
  const dispatch = useDispatch();
  const { request } = useFetch();

  const [loading, setLoading] = useState(true);
  const [focused, setFocused] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");
  const [initialData, setInitialData] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archived, setArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [unArchiving, setUnarchiving] = useState(false);
  const [field, setField] = useState("");
  const [direction, setDirection] = useState("");

  const handleModalClose = () => setConfirmDeleteOpen(false);

  const data = useSelector(
    ({ settingsTests: { settingsTestsList } }) => settingsTestsList
  );

  const fetchTests = () => {
    setLoading(true);
    request(getTests, search, archived, field, field && direction)
      .then((data) => {
        if (!data) return;
        dispatch(setTestsList(data));
        setInitialData(data);
      })
      .finally(() => {
        setArchivedLoading(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchTests();
    // eslint-disable-next-line
  }, [search, archived, field, direction]);

  const showArchived = () => {
    setArchivedLoading(true);
    setArchived(!archived);
  };

  const unArchiveDisabled = useMemo(
    () => Object.values(selected).filter(Boolean).length !== 1,
    [selected]
  );

  const handleUnarchive = () => {
    const testID = getSelectedId(selected);
    const status = (data.find(({ id }) => id === testID) || {}).status;
    if (status !== "archived") return;
    setUnarchiving(true);
    request(modifyTest, { status: "active" }, testID)
      .then((data) => {
        if (!data) return;
        dispatch(unArchiveTest(data));
        setSelected({});
      })
      .finally(() => setUnarchiving(false));
  };

  const handleDelete = (id, setSubmitting) => {
    setSubmitting(true);
    request(deleteTest, id)
      .then((data) => {
        if (!data) return;
        if (archived) {
          request(getTestById, id).then(
            (data) => data && dispatch(unArchiveTest(data))
          );
        } else dispatch(removeTest(id));
      })
      .finally(() => {
        setSubmitting(false);
        setConfirmDeleteOpen(false);
      });
  };

  const renderButtons = () => (
    <>
      <div className="mr-10">
        <Link
          to={{
            pathname: `/add-test`,
          }}
        >
          <ListButton label="New" />
        </Link>
      </div>
      {archived && (
        <UnarchiveButton
          handleClick={handleUnarchive}
          disabled={unArchiveDisabled}
          loading={unArchiving}
        />
      )}
    </>
  );

  const handleFocus = () => setFocused(true);

  const handleOpenModal = (id) => {
    setConfirmDeleteOpen(true);
    setSelectedItem(id);
  };

  const KEY_MAP = {
    "Test Code": data.map((item) => item.code),
    "Test Name": data.map((item) => item.name),
    "Test Category": data.map((item) => item.category),
  };

  const resetSearch = () => {
    dispatch(setTestsList(initialData));
    setSearch("");
  };

  function renderHeaderWithSorting(headings) {
    return headings.map(([key, header]) => (
      <th
        key={key}
        style={sortHeaderStyle}
        className="px-5 text-nowrap"
        onClick={() => {
          setDirection(
            key !== field ? "desc" : direction === "desc" ? "asc" : "desc"
          );
          setField(key);
        }}
      >
        <span style={{ cursor: "pointer" }}>{header}</span>
        {key === field && <TableSortLabel active direction={direction} />}
      </th>
    ));
  }

  return (
    <div className="row justify-content-center mt-10">
      <div className="col-12">
        {confirmDeleteOpen && (
          <Modal
            isOpen={confirmDeleteOpen}
            submitable
            onClose={handleModalClose}
            maxWidth="sm"
            modalContent={
              <ConfirmDelete
                handleClose={handleModalClose}
                handleSubmit={handleDelete}
                id={selectedItem}
                name={(data.find(({ id }) => id === selectedItem) || {}).name}
              />
            }
          />
        )}
        <div>
          <SearchBar
            onSearch={(data) => handleSearch(data, searchMap, setSearch)}
            clearSearch={resetSearch}
            keyMap={KEY_MAP}
            placeholder="Filter Tests..."
            elevation={2}
            chipBackgroundColor="#E8F5E1"
            chipColor="#255915"
            focused={focused}
            setFocused={setFocused}
          />
        </div>
        <div className="bg-white rounded py-7 px-10">
          <ListHeader
            title="Tests"
            renderButtons={renderButtons}
            handleFocus={handleFocus}
          />
          <ArchiveCheckbox
            archivedLoading={archivedLoading}
            archived={archived}
            showArchived={showArchived}
          />
          <Datagrid
            data={data}
            headings={HEADINGS}
            renderRow={renderTestsRow}
            renderHeaderWithSorting={renderHeaderWithSorting}
            editable
            deletable
            selectable
            selected={selected}
            setSelected={setSelected}
            handleDelete={handleOpenModal}
            loading={loading}
            link="tests"
          />
        </div>
      </div>
    </div>
  );
};
